import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, Stack } from '@mui/material';
//import SVG from 'react-inlinesvg';
//import { BRAND_COLOR } from '../../../../common/constants/colors';
import { NWClient } from '../../../../client/NWClient';
//import { getResourceLink } from '../../../../common/utils';
import SubscriptionItem from './SubscriptionItem/SubscriptionItem';
import { Helpers } from '../../../../common/helpers/helpers';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../../common/constants/routes';
import { Trans, useTranslation } from 'react-i18next';
import { useErrorMessage } from '../../../../common/hooks/useErrorMessage';

const DefaultMessage = () => {
  const { t } = useTranslation();
  return (
    <Box component='p'>
      {t('messages.noActiveSubscription')}&nbsp;
      <Link
        id='subscriptionDetailsTabPricingOptionsLink'
        className='link-brand-color'
        to={ROUTES.PRICING}
      >
        {t('links.pricingOptions')}
      </Link>
      .
    </Box>
  );
};

// amount_decimal is used both as a string (parseFloat/.length) and as a number (/100),
// so it is intentionally left loose here.
type PlanState = {
  amount_decimal: any;
  currency: string;
};

export const SubscriptionDetailsTab = (/*{ currentTab }: TabProps*/) => {
  const { getErrorMessage } = useErrorMessage();
  const [loading, setLoading] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<{
    discount?: {
      start: number;
      end: number;
      coupon: {
        duration_in_months: number;
        amount_off?: number;
        percent_off?: number;
        name: string;
      };
    };
    status?: string;
    current_period_end?: string;
    current_period_start?: string;
    start_date?: string;
    plan?: string | number;
  } | null>(null);
  const [planName, setPlanName] = useState('');
  const [plan, setPlan] = useState<PlanState | null>(null);
  const [errorTextArray, setErrorTextArray] = useState<{ [key: string]: string | object }[]>([]);

  const setChargeAmount = () => {
    const couponsForPricesWithoutTaxesIncluding = ['ESSA35']; //For now we calculate with Australian GST for Australian associations
    if (subscription && plan) {
      if (!subscription.discount) {
        return plan.amount_decimal.length > 0
          ? (parseFloat(plan.amount_decimal) / 100).toFixed(2)
          : (plan.amount_decimal / 100).toFixed(2);
      } else {
        if (subscription.discount.coupon.amount_off) {
          return plan.amount_decimal.length > 0
            ? (
                parseFloat(plan.amount_decimal) / 100 -
                subscription.discount.coupon.amount_off / 100
              ).toFixed(2)
            : (plan.amount_decimal / 100 - subscription.discount.coupon.amount_off / 100).toFixed(
                2
              );
        }
        if (subscription.discount.coupon.percent_off) {
          const amount =
            plan.amount_decimal.length > 0
              ? parseFloat(plan.amount_decimal) / 100
              : plan.amount_decimal / 100;
          return couponsForPricesWithoutTaxesIncluding.includes(subscription.discount.coupon.name)
            ? Math.round(
                (amount + amount * 0.1) /*Australian GST*/ *
                  ((100 - subscription.discount.coupon.percent_off) / 100) *
                  100
              ) / 100
            : Math.round(
                (amount - amount * (subscription.discount.coupon.percent_off / 100)) * 100
              ) / 100;
        }
      }
    } else {
      return '0';
    }
  };
  const chargeAmount = setChargeAmount();
  const chargeCurrency = subscription && plan ? plan.currency : '';

  useEffect(() => {
    NWClient.getCurrentCompany()
      .then((res) => {
        if (Array.isArray(res) && res.length > 0 && res[0].stripe_subscription) {
          setSubscription(res[0].stripe_subscription);
          setPlanName((res[0].stripe_product_name ?? '').replace('Business ', ''));
          NWClient.get('plan', String(res[0].stripe_subscription.plan))
            .then((res) => {
              setPlan(res as PlanState);
            })
            .catch((error) => {
              Helpers.handleRequestError(
                error.response,
                setErrorTextArray,
                setLoading,
                getErrorMessage(error)
              );
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        Helpers.handleRequestError(
          error.response,
          setErrorTextArray,
          setLoading,
          getErrorMessage(error)
        );
      });
  }, [getErrorMessage]);

  return (
    <Grid component='div'>
      {loading ? (
        <Grid component='div' container style={{ justifyContent: 'center', marginBottom: '16px' }}>
          <CircularProgress />
        </Grid>
      ) : (
        <Grid component='div'>
          <Box
            className='setting-panel-body'
            sx={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}
          >
            {subscription && subscription.status !== 'canceled' ? (
              <SubscriptionItem
                planName={planName}
                currentPeriodEnd={
                  Helpers.formatDBDateToUI(subscription.current_period_end ?? '') ?? ''
                }
                currentPeriodStart={
                  Helpers.formatDBDateToUI(subscription.current_period_start ?? '') ?? ''
                }
                status={subscription.status ?? ''}
                startDate={Helpers.formatDBDateToUI(subscription.start_date ?? '') ?? ''}
                errorTextArray={errorTextArray as { [key: string]: string }[]}
                discount={subscription.discount}
                chargeDate={Helpers.formatDBDateToUI(subscription.current_period_end ?? '') ?? ''}
                chargeAmount={String(chargeAmount)}
                chargeCurrency={chargeCurrency}
              />
            ) : (
              <DefaultMessage />
            )}
            <Stack
              sx={{
                display: { xs: 'block', md: 'flex' },
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: '16px',
              }}
              className='info-row'
            >
              <div className='disclaimer-text'>
                <Trans
                  i18nKey='settings.termsAndConditions'
                  components={{
                    a: <a />,
                  }}
                />
              </div>
              <div className='disclaimer-text'>
                <Trans
                  i18nKey='settings.privacyPolicy'
                  components={{
                    a: <a />,
                  }}
                />
              </div>
              <div className='disclaimer-text'>
                <Trans
                  i18nKey='settings.signatureDisclosure'
                  components={{
                    a: <a />,
                  }}
                />
              </div>
            </Stack>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
